import React, { useRef } from 'react';
import { AnimatePresence } from 'framer-motion';
import { GlassCard } from '../GlassCard';
import { GlassInput } from '../GlassInput';
import { GlassPill } from '../GlassPill';
import { GlassCTA } from '../GlassCTA';

interface GoalsStepProps {
  goals: string[];
  onGoalsChange: (goals: string[]) => void;
  onNext: () => void;
}

export function GoalsStep({ goals, onGoalsChange, onNext }: GoalsStepProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [currentInput, setCurrentInput] = React.useState('');

  const addGoal = () => {
    const text = currentInput.trim();
    if (text && !goals.includes(text)) {
      onGoalsChange([...goals, text]);
      setCurrentInput('');
      inputRef.current?.focus();
    }
  };

  const removeGoal = (index: number) => {
    onGoalsChange(goals.filter((_, i) => i !== index));
  };

  return (
    <GlassCard>
      <div className="flex flex-col items-center text-center">
        <h1
          className="text-[28px] font-semibold text-white tracking-tight"
          style={{ textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)' }}
        >
          What are you working toward in the next few months?
        </h1>
        <p
          className="mt-3 text-base"
          style={{ color: 'rgba(255, 255, 255, 0.6)' }}
        >
          Big or small. List as many as you want.
        </p>

        <div className="mt-8 w-full max-w-md">
          <GlassInput
            ref={inputRef}
            autoFocus
            value={currentInput}
            onChange={(e) => setCurrentInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addGoal();
              }
            }}
            placeholder="e.g. Launch Q2 campaign"
          />
          <p
            className="mt-2 text-xs"
            style={{ color: 'rgba(255, 255, 255, 0.3)' }}
          >
            Press Enter to add
          </p>
        </div>

        {goals.length > 0 && (
          <div className="mt-4 flex flex-wrap justify-center gap-2">
            <AnimatePresence mode="popLayout">
              {goals.map((text, i) => (
                <GlassPill
                  key={text}
                  text={text}
                  onRemove={() => removeGoal(i)}
                />
              ))}
            </AnimatePresence>
          </div>
        )}

        <div className="mt-10">
          <GlassCTA onClick={onNext} disabled={goals.length < 1}>
            These are my goals
          </GlassCTA>
        </div>
      </div>
    </GlassCard>
  );
}
